import { CSSProperties, RefObject, useCallback, useLayoutEffect, useState } from 'react';

/**
 * `common/overlayPosition` (M-V4-A, architecture-ux-v4.md §1.2).
 *
 * DAS gemeinsame Positions-Primitive für alle portal-basierten Overlays der
 * Tabellen-Welt (StatusChip-Popover §1.4 und RowActionsMenu-Popover §1.3). Ein
 * Overlay wird per `createPortal(document.body)` mit `position: fixed` an den
 * Trigger geankert — dadurch entkommt es dem clippenden `overflow-x-auto`-
 * Scrollport der `DataTable` (Ursache von D-V4-2: „versetzt und verdeckt").
 *
 * Der reine Rechenkern (`computeAnchoredPosition`) ist der Test-Vertrag
 * (Resolver-Pattern, golden-rules): keine DOM-Abhängigkeit, deterministisch,
 * wirft nie. Der dünne Hook (`useAnchoredPosition`) misst per
 * `getBoundingClientRect`/`offsetWidth` und positioniert bei Scroll (capture!)
 * und Resize neu.
 */

export type OverlayPlacement = 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end';

export interface OverlayRect {
  top: number;
  bottom: number;
  left: number;
  right: number;
  width: number;
  height: number;
}

export interface OverlaySize {
  width: number;
  height: number;
}

export interface OverlayViewport {
  width: number;
  height: number;
}

export interface AnchoredPosition {
  /** px, Viewport-Koordinaten (für `position: fixed`). */
  top: number;
  /** px, Viewport-Koordinaten. */
  left: number;
  /** Tatsächlich gewähltes Placement nach Flip. */
  placement: OverlayPlacement;
}

/** Abstand Trigger ↔ Overlay (entspricht heutigem `mt-1`/`mb-1`). */
const OFFSET = 4;
/** Mindest-Rand zum Viewport-Rand beim Clampen. */
const MARGIN = 8;

/**
 * PURE. Rechnet die `fixed`-Koordinaten eines Overlays relativ zum Trigger-Rect.
 *
 * Flip-Regeln (Verhaltens-Parität zum bisherigen StatusChip):
 * - vertikal, bevorzugt unten: wenn `spaceBelow < overlayH` UND
 *   `spaceAbove > spaceBelow` → oben. Sonst bleibt es unten.
 * - vertikal, bevorzugt oben: spiegelbildlich.
 * - horizontal `-start`: `left` am Trigger-`left`; ragt das Overlay rechts über
 *   `viewportW - MARGIN` hinaus → clamp nach links (MARGIN-Rand rechts).
 * - horizontal `-end`: `left = right - overlayW`; ragt es links über `MARGIN`
 *   hinaus → clamp auf `MARGIN`.
 *
 * Wirft NIE — 0-Rects (JSDOM) ergeben deterministisch `{top: OFFSET, left: 0}`.
 */
export function computeAnchoredPosition(
  triggerRect: OverlayRect,
  overlaySize: OverlaySize,
  viewport: OverlayViewport,
  preferred: OverlayPlacement,
): AnchoredPosition {
  const preferTop = preferred.startsWith('top');
  const isEnd = preferred.endsWith('end');

  const spaceBelow = viewport.height - triggerRect.bottom;
  const spaceAbove = triggerRect.top;

  let vertical: 'top' | 'bottom';
  if (preferTop) {
    vertical = spaceAbove < overlaySize.height && spaceBelow > spaceAbove ? 'bottom' : 'top';
  } else {
    vertical = spaceBelow < overlaySize.height && spaceAbove > spaceBelow ? 'top' : 'bottom';
  }

  const top =
    vertical === 'bottom'
      ? triggerRect.bottom + OFFSET
      : triggerRect.top - OFFSET - overlaySize.height;

  let left: number;
  if (isEnd) {
    left = triggerRect.right - overlaySize.width;
    if (left < MARGIN) left = MARGIN;
  } else {
    left = triggerRect.left;
    if (left + overlaySize.width > viewport.width - MARGIN) {
      left = viewport.width - MARGIN - overlaySize.width;
    }
  }

  const placement = `${vertical}-${isEnd ? 'end' : 'start'}` as OverlayPlacement;
  return { top, left, placement };
}

export interface UseAnchoredPositionResult {
  style: CSSProperties;
  placement: OverlayPlacement;
}

/**
 * Misst Trigger + Overlay und liefert `{style, placement}` für ein `position:
 * fixed`-Overlay. Neupositionierung bei `scroll` (capture: true — fängt auch den
 * inneren `overflow-x-auto`-Scrollport der DataTable) und `resize`, nur solange
 * `open`. In JSDOM liefern `getBoundingClientRect`/`offsetWidth` 0 ⇒ stabile,
 * deterministische Position (wirft nie).
 */
export function useAnchoredPosition(
  open: boolean,
  triggerRef: RefObject<HTMLElement | null>,
  overlayRef: RefObject<HTMLElement | null>,
  preferred: OverlayPlacement,
): UseAnchoredPositionResult {
  const [position, setPosition] = useState<AnchoredPosition>({
    top: 0,
    left: 0,
    placement: preferred,
  });

  const update = useCallback(() => {
    const trigger = triggerRef.current;
    const overlay = overlayRef.current;
    if (!trigger || !overlay) return;
    const rect = trigger.getBoundingClientRect();
    const triggerRect: OverlayRect = {
      top: rect.top,
      bottom: rect.bottom,
      left: rect.left,
      right: rect.right,
      width: rect.width,
      height: rect.height,
    };
    const overlaySize: OverlaySize = {
      width: overlay.offsetWidth,
      height: overlay.offsetHeight,
    };
    const viewport: OverlayViewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };
    setPosition(computeAnchoredPosition(triggerRect, overlaySize, viewport, preferred));
  }, [triggerRef, overlayRef, preferred]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    update();
    const onScroll = () => update();
    const onResize = () => update();
    // capture: true fängt Scroll-Events auch aus verschachtelten Scroll-Containern.
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, update]);

  return {
    style: { position: 'fixed', top: position.top, left: position.left },
    placement: position.placement,
  };
}
